import type { StateCreator } from 'zustand';
import type { UserSettings } from '../../types';
import type { DraftModelSummary } from '../../studioTypes';
import type { VoyagerState } from '../useVoyagerStore';
import { getDraftsForModel } from '../../apis';
import { loadUserSettings, saveUserSettings } from '../helpers/persistenceHelpers';

export interface SettingsSlice {
  userSettings: UserSettings;
  activeDraftId: string | null;
  activeDraftName: string | null;
  availableDrafts: DraftModelSummary[];

  updateUserSettings: (updates: Partial<UserSettings>) => void;
  setModelSource: (source: 'production' | 'draft', draftId?: string, draftName?: string) => void;
  fetchAvailableDrafts: () => Promise<void>;
}

export const createSettingsSlice: StateCreator<
  VoyagerState, [], [], SettingsSlice
> = (set, get) => ({
  userSettings: loadUserSettings(),
  activeDraftId: null,
  activeDraftName: null,
  availableDrafts: [],

  updateUserSettings: (updates: Partial<UserSettings>) => {
    const merged = { ...get().userSettings, ...updates };
    saveUserSettings(merged);
    set({ userSettings: merged });
  },

  setModelSource: (source: 'production' | 'draft', draftId?: string, draftName?: string) => {
    const { selectedPerspective } = get();
    const newSettings = { ...get().userSettings, modelSource: source };
    saveUserSettings(newSettings);

    if (source === 'draft' && draftId) {
      set({
        userSettings: newSettings,
        activeDraftId: draftId,
        activeDraftName: draftName || null,
      });
    } else {
      set({
        userSettings: newSettings,
        activeDraftId: null,
        activeDraftName: null,
      });
    }

    // Reload the model with the new source
    if (selectedPerspective) {
      get().loadModel(selectedPerspective);
    }
  },

  fetchAvailableDrafts: async () => {
    const { semanticModel } = get();
    const modelName = semanticModel?.model;
    if (!modelName) {
      set({ availableDrafts: [] });
      return;
    }
    try {
      const drafts = await getDraftsForModel(modelName);
      set({ availableDrafts: drafts });
    } catch (err: any) {
      console.error('Failed to fetch available drafts:', err?.message);
      set({ availableDrafts: [] });
    }
  },
});
